import React, { useState, useRef } from 'react';
import { hot } from 'react-hot-loader';
import { connect } from 'react-redux';
import Button from '../Elements/Button/Button';
import Newsletter from '../Footer/Newsletter';
import ZohoForm from '../../hocs/ZohoForm/ZohoForm';
import { imageSizeTranslation } from '../../utils/image';
import { translate } from '../../utils/locale';
import { isDismissed, useNotificationState, useFocusTrap } from '../../utils/notification';
import { cookieConsentDisabled } from '../../config';
import s from './NotificationPopup.scss';

// Shows the current type ("Private"), or on hover the one clicking lets you switch to ("Trade?").
const consumerTypeLabel = (setup, hovered) => {
	const isPrivate = setup.consumerType === 'private';
	if (!hovered) return translate(setup, isPrivate ? 'consumerPrivate' : 'consumerTrade');
	return `${translate(setup, isPrivate ? 'consumerTrade' : 'consumerPrivate')}?`;
};

const PopupMedia = ({ setup, image, imageMobile, film, filmMobile }) => {
	const video = setup.breakPoint === 'mobile' ? filmMobile || film : film || filmMobile;
	if (video) return <video className={s.popup_media} src={video.url} autoPlay loop muted playsInline />;
	if (!image && !imageMobile) return null;

	return (
		<picture className={s.popup_media}>
			{ imageMobile && <source {...imageSizeTranslation('4', imageMobile, '-', false)} media="(orientation: portrait)" /> }
			<img alt="" {...imageSizeTranslation('4', image || imageMobile, '-', false)} />
		</picture>
	);
};

const EmecoLogo = () => (
	<svg width="89" height="13" viewBox="0 0 89 13" fill="none" xmlns="http://www.w3.org/2000/svg">
		<path d="M7.29454 12.184C12.0555 12.184 13.5045 11.379 13.5045 8.458V8.228H12.3775V8.412C12.3775 10.551 11.5725 11.149 7.22554 11.149H6.78854C2.02754 11.149 1.19954 10.39 1.19954 6.802V6.549H13.4125V5.33C13.4125 1.696 12.2395 0.775999 7.08754 0.775999H6.60454C1.29154 0.775999 0.0265353 1.65 0.0265353 6.319V6.595C0.0265353 11.172 1.19954 12.184 6.78854 12.184H7.29454ZM1.22254 5.537C1.31454 2.432 1.98154 1.811 6.67354 1.811H7.04154C11.5725 1.811 12.2625 2.455 12.2625 5.169V5.537H1.22254ZM39.1733 5.284C39.1733 1.696 38.0693 0.775999 34.0213 0.775999H33.6073C30.1113 0.775999 28.8693 2.133 28.4093 3.582H28.3863C28.0183 1.673 27.2133 0.775999 23.4183 0.775999H23.0733C19.9453 0.775999 18.6113 1.742 17.9903 3.237H17.9673V0.96H16.7943V12H17.9673V5.859C17.9673 2.708 19.6923 1.811 22.8433 1.811H23.1883C26.5693 1.811 27.3973 2.294 27.3973 5.859V12H28.5703V6.066C28.5703 2.777 30.2263 1.811 33.3773 1.811H33.7683C37.3103 1.811 38.0003 2.432 38.0003 5.882V12H39.1733V5.284ZM49.6109 12.184C54.3719 12.184 55.8209 11.379 55.8209 8.458V8.228H54.6939V8.412C54.6939 10.551 53.8889 11.149 49.5419 11.149H49.1049C44.3439 11.149 43.5159 10.39 43.5159 6.802V6.549H55.7289V5.33C55.7289 1.696 54.5559 0.775999 49.4039 0.775999H48.9209C43.6079 0.775999 42.3429 1.65 42.3429 6.319V6.595C42.3429 11.172 43.5159 12.184 49.1049 12.184H49.6109ZM43.5389 5.537C43.6309 2.432 44.2979 1.811 48.9899 1.811H49.3579C53.8889 1.811 54.5789 2.455 54.5789 5.169V5.537H43.5389ZM65.8957 12.184C70.7487 12.184 72.1517 11.379 72.1517 8.343V8.044H71.0247V8.251C71.0247 10.551 70.1507 11.149 65.8727 11.149H65.3897C60.5367 11.149 59.7777 10.436 59.7777 6.664V6.296C59.7777 2.478 60.4907 1.811 65.3897 1.811H65.8727C70.0587 1.811 70.9327 2.317 70.9327 4.594V4.755H72.0597V4.502C72.0597 1.466 70.8177 0.775999 65.8957 0.775999H65.3667C59.8467 0.775999 58.6047 1.627 58.6047 6.296V6.664C58.6047 11.287 59.8237 12.184 65.3667 12.184H65.8957ZM81.7188 11.149C76.7738 11.149 76.1068 10.574 76.1068 6.664V6.296C76.1068 2.386 76.7508 1.811 81.7188 1.811H82.2018C87.1468 1.811 87.7908 2.386 87.7908 6.296V6.664C87.7908 10.574 87.1468 11.149 82.2018 11.149H81.7188ZM82.2248 12.184C87.9058 12.184 88.9638 11.333 88.9638 6.664V6.296C88.9638 1.604 87.9058 0.775999 82.2248 0.775999H81.6958C76.0148 0.775999 74.9338 1.604 74.9338 6.296V6.664C74.9338 11.333 76.0148 12.184 81.6958 12.184H82.2248Z" fill="black" />
	</svg>
);

/*
	A modal that minimizes into the bottom-right stack as a Contact-style button.
	variant 'popup'    - header.popup: image/film + zoho form or newsletter; waits for cookie consent.
	variant 'consumer' - header.consumer: private/trade choice, stored as its dismissal cookie. Once
	                     chosen it minimizes to a button showing the current type, to change it later.
	A popup minimizes when it has a minimizeTitle; otherwise closing dismisses it for cookieDuration.
*/
const NotificationPopup = ({ item, setup, variant = 'popup', cookieConsent, onConsumerChoice = () => {} }) => {
	const isConsumer = variant === 'consumer';
	// Popups (e.g. newsletter capture) stay hidden, and their delay doesn't start, until the
	// visitor has made a cookie-consent decision. cookieConsent is null until then.
	const consentReady = isConsumer || cookieConsentDisabled || Array.isArray(cookieConsent);
	const { expanded, visible, exiting, expand, minimize, dismiss } = useNotificationState(item, { setup, autoExpand: true, enabled: consentReady, persistent: isConsumer });
	const [hovered, setHovered] = useState(false);
	const dialogRef = useRef(null);

	// The consumer choice can't be skipped, only closed again once a type has been chosen.
	const closable = !isConsumer || isDismissed(item, setup);
	let close = () => dismiss();
	if (isConsumer) close = () => dismiss(setup.consumerType);
	else if (item.minimizeTitle) close = minimize;
	const isOpen = visible && consentReady && expanded;

	useFocusTrap(dialogRef, isOpen, { onEscape: closable ? close : null });

	if (!visible || !consentReady) return null;

	if (!expanded) {
		const minimizedLabel = isConsumer ? consumerTypeLabel(setup, hovered) : item.minimizeTitle && translate(setup, item.minimizeTitle);
		if (!minimizedLabel) return null;
		return (
			<div className={s.popup_minimized}>
				<Button type="button" variantClasses={['button__card']} clickEventHandler={() => { setHovered(false); expand(); }} mouseEnterEventHandler={() => setHovered(true)} mouseLeaveEventHandler={() => setHovered(false)} ariaExpanded={false}>
					{ minimizedLabel }
				</Button>
			</div>
		);
	}

	const chooseConsumerType = (e) => {
		const { value } = e.currentTarget;
		dismiss(value);
		onConsumerChoice(value);
	};

	const newsletterSubmitted = (autoRemove) => {
		if (autoRemove) window.setTimeout(() => dismiss(), 1500);
	};

	const media = isConsumer
		? { image: item.images?.imageDesktop, imageMobile: item.images?.imageMobile }
		: { image: item.image, imageMobile: item.imageMobile, film: item.film, filmMobile: item.filmMobile };

	return (
		// A plain wrapper, not a Fragment: every other stack item (minimized or not) is exactly
		// one direct child of .notificationStack, and the stack's own collapsed/expanded layout
		// styling (see Header.scss) depends on that. A Fragment here would flatten the screen and
		// dialog in as two siblings instead, which - being real elements - would both inherit
		// that styling and lose their own position:fixed layout entirely. Fine either way here:
		// with nothing else in normal flow, this wrapper collapses to nothing, however it's positioned.
		<div>
			{
				closable
					? <button type="button" className={[s.popup_screen, exiting ? s.popup_screen__exiting : null].filter(Boolean).join(' ')} aria-label="Close popup" onClick={close} />
					: <div className={[s.popup_screen, exiting ? s.popup_screen__exiting : null].filter(Boolean).join(' ')} />
			}
			<div ref={dialogRef} role="dialog" aria-modal="true" aria-label={item.name} className={[s.popup_dialog, isConsumer ? s.popup_dialog__consumer : null, exiting ? s.popup_dialog__exiting : null].filter(Boolean).join(' ')}>
				<PopupMedia setup={setup} {...media} />
				{
					isConsumer && (
						<div className={s.popup_consumer}>
							<EmecoLogo />
							<h4 className={s.popup_consumer_title}>{ translate(setup, item.title) }</h4>
							<p className={s.popup_consumer_description}>{ translate(setup, item.description) }</p>
							<div className={s.popup_consumer_choices}>
								<Button type="button" clickEventHandler={chooseConsumerType} value="private">{ translate(setup, 'consumerPrivate') }</Button>
								<Button type="button" clickEventHandler={chooseConsumerType} value="trade">{ translate(setup, 'consumerTrade') }</Button>
							</div>
						</div>
					)
				}
				{
					!isConsumer && item.zohoFields && (
						<div className={[s.popup_content, s.popup_content__zoho].join(' ')}>
							<ZohoForm setup={setup} htmlBlob={item.zohoFields} multistep callback={() => dismiss()} />
						</div>
					)
				}
				{
					!isConsumer && !item.zohoFields && item.includeNewsletter && (
						<div className={s.popup_content}>
							<div className={s.popup_newsletter}>
								<Newsletter popup callback={newsletterSubmitted} isClosing={exiting} closePopup={close} />
							</div>
						</div>
					)
				}
				{
					closable && (
						<button type="button" className={s.popup_close} onClick={close} aria-label="Close popup">
							<span className={s.popup_close_icon} />
						</button>
					)
				}
			</div>
		</div>
	);
};

const mapState = ({ environment }) => ({ cookieConsent: environment.cookieConsent });

export default hot(module)(connect(mapState)(NotificationPopup));
